import { CallExpressionShape, TemplateValueShape } from '@servicenow/sdk-build-core'
import type { Compiler, Diagnostics, ObjectShape, Shape } from '@servicenow/sdk-build-core'
import { DATAPILL_CALLEE } from '../constants/playbook-plugin-constants'
import { resolveTableFieldNames } from './table-field-resolver'

/**
 * Column types whose values reference fields on a dependent table and should
 * be validated. The callee name matches the factory function used in column
 * definitions:
 *
 * - FieldListColumn     → value is an array of field names
 * - TemplateValueColumn → value is a 'field=value^field=value' string
 */
const FIELD_DEPENDENT_COLUMN_TYPES = new Set(['FieldListColumn', 'TemplateValueColumn'])

/**
 * Validates that field references in dependent column values actually exist on
 * the table identified by the column's `dependent` source property.
 *
 * ## What this validates
 *
 * Columns like FieldListColumn and TemplateValueColumn declare a `dependent`
 * property that names a sibling column providing the table context:
 *
 * ```ts
 * // Column schema (in ActivityType, Action, or Subflow definition):
 * associated_table: TableNameColumn({ ... }),
 * record_fields:    FieldListColumn({ dependent: 'associated_table', ... }),
 *
 * // User's Activity() call:
 * associated_table: 'change_request',
 * record_fields:    ['short_description', 'requested_by'],  // ← validated
 * ```
 *
 * ## When validation is skipped
 *
 * - The dependent source value is not a string literal (e.g. dataPill)
 * - The column value itself is a dataPill (runtime-determined)
 * - The table name doesn't resolve to a known table type
 * - Individual field list elements that aren't string literals
 *
 * @param columnDefinitions - Column schema from the ActivityType or backing
 *     Action/Subflow (e.g. `{ record_fields: FieldListColumn({...}), ... }`)
 * @param userValues - Actual values from the user's Activity() call
 * @param compiler   - The ts-morph compiler from the plugin context
 * @param rootDir    - Project root directory for temp file placement
 * @param diagnostics - Diagnostics reporter for emitting errors
 */
export function validateDependentFields(
    columnDefinitions: ObjectShape,
    userValues: ObjectShape,
    compiler: Compiler,
    rootDir: string,
    diagnostics: Diagnostics
): void {
    // Only inspect columns that the user actually provided values for.
    for (const propName of userValues.keys()) {
        const columnShape = columnDefinitions.get(propName, false)
        if (!columnShape) {
            continue
        }

        // Column definitions are factory calls like:
        //   FieldListColumn({ dependent: 'associated_table', ... })
        const callExpr = columnShape.if(CallExpressionShape)
        if (!callExpr) {
            continue
        }

        // Only validate column types that reference table fields.
        // DocumentIdColumn also has `dependent` but its value is a record
        // reference, not a field name — different kind of validation.
        const callee = callExpr.getCallee()
        if (!FIELD_DEPENDENT_COLUMN_TYPES.has(callee)) {
            continue
        }

        // Extract `dependent` from the column config — this names the sibling
        // property that provides the table.
        const columnConfig = callExpr.getArgument(0)?.ifObject()
        if (!columnConfig) {
            continue
        }
        const dependentFieldName = columnConfig.get('dependent', false)?.ifString()?.getValue()
        if (!dependentFieldName) {
            continue
        }

        // Read the table name from the sibling property in the user's values.
        // If the value isn't a string literal (e.g. a dataPill), we can't
        // resolve the table statically — skip.
        const tableNameShape = userValues.get(dependentFieldName)
        if (!tableNameShape) {
            continue
        }
        const tableName = tableNameShape.ifString()?.getValue()
        if (!tableName) {
            continue
        }

        // Resolve the table's field names via the TypeScript type system.
        // Returns undefined for tables that aren't in the SDK type registry.
        const validFields = resolveTableFieldNames(compiler, rootDir, tableName)
        if (!validFields) {
            continue
        }

        const valueShape = userValues.get(propName)!

        // Skip validation when the value is a dataPill — the field names are
        // determined at runtime and cannot be checked statically.
        if (isDataPill(valueShape)) {
            continue
        }

        if (callee === 'FieldListColumn') {
            validateFieldListValues(valueShape, validFields, propName, tableName, diagnostics)
        } else if (callee === 'TemplateValueColumn') {
            validateTemplateValueKeys(valueShape, validFields, propName, tableName, diagnostics)
        }
    }
}

/**
 * Validates each element in a FieldListColumn array value.
 *
 * Given `record_fields: ['short_description', 'invalid_field']`, checks that
 * each string literal is a known field of the associated table. Non-literal
 * elements are silently skipped.
 *
 * Handles both forms:
 * - Plain array:    `['short_description', 'requested_by']`
 * - FieldList call: `FieldList<'change_request'>(['short_description', 'requested_by'])`
 */
function validateFieldListValues(
    valueShape: Shape,
    validFields: Set<string>,
    propName: string,
    tableName: string,
    diagnostics: Diagnostics
): void {
    // Plain array literal: record_fields: ['short_description']
    // FieldList wrapper:   record_fields: FieldList<'change_request'>(['short_description'])
    //   → CallExpressionShape with callee 'FieldList', first arg is the array
    const arrayShape = valueShape.ifArray() ?? valueShape.if(CallExpressionShape)?.getArgument(0)?.ifArray()
    if (!arrayShape) {
        return
    }

    for (const element of arrayShape.getElements()) {
        const fieldName = element.ifString()?.getValue()
        if (!fieldName) {
            continue
        }

        if (!validFields.has(fieldName)) {
            diagnostics.error(element, `'${propName}': field '${fieldName}' does not exist on table '${tableName}'.`)
        }
    }
}

/**
 * Validates field names in a TemplateValueColumn value.
 *
 * Handles both forms:
 * - Plain string / template: `'field=value^field=value'` — field names are
 *   extracted from the left side of each `=` in the caret-separated pairs.
 * - TemplateValue call:      `TemplateValue({ field: value, ... })` — field
 *   names are the object keys directly.
 */
function validateTemplateValueKeys(
    valueShape: Shape,
    validFields: Set<string>,
    propName: string,
    tableName: string,
    diagnostics: Diagnostics
): void {
    // Plain string or template expression: 'field=value^field=value'
    // (TemplateExpressionShape extends StringShape, so ifString() covers both)
    const fieldNames =
        extractFieldNamesFromString(valueShape) ??
        // TemplateValue wrapper: TemplateValue({ field: value, ... })
        extractFieldNamesFromTemplateValue(valueShape)

    if (!fieldNames) {
        return
    }

    for (const fieldName of fieldNames) {
        if (!validFields.has(fieldName)) {
            diagnostics.error(valueShape, `'${propName}': field '${fieldName}' does not exist on table '${tableName}'.`)
        }
    }
}

/** Extracts field names from a 'field=value^field=value' string. */
function extractFieldNamesFromString(valueShape: Shape): string[] | undefined {
    const strValue = valueShape.ifString()?.getValue()
    if (!strValue) {
        return undefined
    }
    const fieldNames: string[] = []
    for (const pair of strValue.split('^')) {
        const eqIndex = pair.indexOf('=')
        if (eqIndex > 0) {
            fieldNames.push(pair.substring(0, eqIndex))
        }
    }
    return fieldNames.length > 0 ? fieldNames : undefined
}

/** Extracts field names (object keys) from a TemplateValue({ ... }) call. */
function extractFieldNamesFromTemplateValue(valueShape: Shape): string[] | undefined {
    if (!(valueShape instanceof TemplateValueShape)) {
        return undefined
    }
    const keys = valueShape.getTemplateValue().keys()
    return keys.length > 0 ? keys : undefined
}

/**
 * Returns true when the shape is a `wfa.playbook.dataPill(...)` call whose
 * value is determined at runtime and cannot be validated statically.
 */
function isDataPill(shape: Shape): boolean {
    const call = shape.if(CallExpressionShape)
    return call !== undefined && call.getCallee() === DATAPILL_CALLEE
}
